import React, { useCallback, useEffect, useState } from 'react';
import { Award, IndianRupee, Plus, Trash2, Pencil, Save, X, TrendingUp, AlertCircle, CheckCircle2, Pause, Play, Calendar } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { usePanelAuth } from './PanelAuthContext';

/**
 * Admin Incentives & Earnings Manager
 *  - Configure incentive slabs (jobs threshold → bonus amount)
 *  - Configure bonus campaigns (per-job-bonus on specific pest / service window)
 *  - View live earnings of every active employee for this month
 *  - Hold / Release a technician's payout
 */
export default function IncentivesAdminTab() {
  const { api } = usePanelAuth();
  const [tab, setTab] = useState('slabs');

  return (
    <div className="space-y-5" data-testid="incentives-admin-tab">
      <div className="inline-flex p-1 bg-slate-100 rounded-lg">
        {[
          { id: 'slabs', label: 'Incentive Slabs', Icon: Award },
          { id: 'campaigns', label: 'Bonus Campaigns', Icon: TrendingUp },
          { id: 'overview', label: 'Live Earnings', Icon: IndianRupee },
          { id: 'holds', label: 'Holds', Icon: Pause },
        ].map(({ id, label, Icon }) => (
          <Button
            key={id}
            variant="ghost"
            onClick={() => setTab(id)}
            data-testid={`inc-subtab-${id}`}
            className={`h-9 px-4 text-xs font-semibold rounded-md inline-flex items-center gap-1.5 ${tab === id ? 'bg-white text-slate-900 shadow' : 'text-slate-600'}`}
          >
            <Icon size={13} /> {label}
          </Button>
        ))}
      </div>

      {tab === 'slabs' && <SlabsPanel api={api} />}
      {tab === 'campaigns' && <CampaignsPanel api={api} />}
      {tab === 'overview' && <EarningsOverviewPanel api={api} />}
      {tab === 'holds' && <HoldsPanel api={api} />}
    </div>
  );
}

// ───── Slabs ─────
function SlabsPanel({ api }) {
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);
  const blank = { label: '', min_jobs: 0, max_jobs: null, bonus_amount: 0, active: true, sort: 0 };
  const [draft, setDraft] = useState(blank);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const r = await api('GET', '/admin/incentive-slabs');
    setRows(r.data?.items || []);
  }, [api]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setBusy(true);
    try {
      const body = { ...draft, min_jobs: Number(draft.min_jobs), max_jobs: draft.max_jobs ? Number(draft.max_jobs) : null, bonus_amount: Number(draft.bonus_amount), sort: Number(draft.sort || 0) };
      if (editing) await api('PATCH', `/admin/incentive-slabs/${editing}`, body);
      else await api('POST', '/admin/incentive-slabs', body);
      setDraft(blank); setEditing(null); load();
    } finally { setBusy(false); }
  };

  const del = async (id) => {
    if (!window.confirm('Delete this slab?')) return;
    await api('DELETE', `/admin/incentive-slabs/${id}`); load();
  };

  return (
    <div className="space-y-4">
      <div className="bg-white border rounded-xl p-4 shadow-sm">
        <div className="text-sm font-semibold text-slate-900 mb-3 flex items-center gap-2"><Award size={16} className="text-emerald-600" /> {editing ? 'Edit slab' : 'Add new slab'}</div>
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
          <div><Label>Label</Label><Input value={draft.label} onChange={(e) => setDraft({ ...draft, label: e.target.value })} placeholder="e.g. Performer" data-testid="slab-label" /></div>
          <div><Label>Min jobs</Label><Input type="number" value={draft.min_jobs} onChange={(e) => setDraft({ ...draft, min_jobs: e.target.value })} data-testid="slab-min-jobs" /></div>
          <div><Label>Max jobs (blank = ∞)</Label><Input type="number" value={draft.max_jobs ?? ''} onChange={(e) => setDraft({ ...draft, max_jobs: e.target.value || null })} data-testid="slab-max-jobs" /></div>
          <div><Label>Bonus ₹</Label><Input type="number" value={draft.bonus_amount} onChange={(e) => setDraft({ ...draft, bonus_amount: e.target.value })} data-testid="slab-bonus" /></div>
          <div className="flex items-end gap-2">
            <Button onClick={save} disabled={busy || !draft.label} className="bg-emerald-600 hover:bg-emerald-700 text-white w-full" data-testid="slab-save"><Save size={14} className="mr-1.5" /> {editing ? 'Update' : 'Add'}</Button>
            {editing && <Button variant="outline" onClick={() => { setEditing(null); setDraft(blank); }}><X size={14} /></Button>}
          </div>
        </div>
      </div>
      <div className="bg-white border rounded-xl shadow-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-600">
            <tr><th className="px-4 py-2 text-left">Label</th><th className="px-4 py-2 text-left">Range</th><th className="px-4 py-2 text-left">Bonus</th><th className="px-4 py-2 text-left">Status</th><th className="px-4 py-2"></th></tr>
          </thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={5} className="text-center py-6 text-slate-400">No slabs yet — add one above.</td></tr>}
            {rows.map((r) => (
              <tr key={r.id} className="border-t" data-testid={`slab-row-${r.id}`}>
                <td className="px-4 py-2 font-semibold">{r.label}</td>
                <td className="px-4 py-2 text-slate-700">{r.min_jobs}{r.max_jobs ? `–${r.max_jobs}` : '+'} jobs</td>
                <td className="px-4 py-2 font-bold text-emerald-700">₹{Number(r.bonus_amount).toLocaleString()}</td>
                <td className="px-4 py-2">{r.active ? <span className="text-green-700 text-xs font-bold">ACTIVE</span> : <span className="text-slate-400 text-xs font-bold">INACTIVE</span>}</td>
                <td className="px-4 py-2 text-right">
                  <Button size="sm" variant="ghost" onClick={() => { setEditing(r.id); setDraft({ label: r.label, min_jobs: r.min_jobs, max_jobs: r.max_jobs, bonus_amount: r.bonus_amount, active: r.active, sort: r.sort || 0 }); }}><Pencil size={14} /></Button>
                  <Button size="sm" variant="ghost" onClick={() => del(r.id)} className="text-red-600"><Trash2 size={14} /></Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ───── Campaigns ─────
function CampaignsPanel({ api }) {
  const blank = { name: '', pest_filter: '', service_filter: '', start_date: '', end_date: '', per_job_bonus: 0, active: true };
  const [rows, setRows] = useState([]);
  const [draft, setDraft] = useState(blank);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const r = await api('GET', '/admin/bonus-campaigns');
    setRows(r.data?.items || []);
  }, [api]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setBusy(true);
    try {
      const body = { ...draft, per_job_bonus: Number(draft.per_job_bonus), pest_filter: draft.pest_filter || null, service_filter: draft.service_filter || null };
      if (editing) await api('PATCH', `/admin/bonus-campaigns/${editing}`, body);
      else await api('POST', '/admin/bonus-campaigns', body);
      setDraft(blank); setEditing(null); load();
    } finally { setBusy(false); }
  };
  const del = async (id) => { if (!window.confirm('Delete campaign?')) return; await api('DELETE', `/admin/bonus-campaigns/${id}`); load(); };

  return (
    <div className="space-y-4">
      <div className="bg-white border rounded-xl p-4 shadow-sm">
        <div className="text-sm font-semibold text-slate-900 mb-3 flex items-center gap-2"><TrendingUp size={16} className="text-amber-600" /> {editing ? 'Edit campaign' : 'New campaign'}</div>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          <div><Label>Name</Label><Input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} placeholder="e.g. July Termite Push" data-testid="camp-name" /></div>
          <div><Label>Pest filter (optional)</Label><Input value={draft.pest_filter} onChange={(e) => setDraft({ ...draft, pest_filter: e.target.value })} placeholder="e.g. termite" /></div>
          <div><Label>Service slug filter (optional)</Label><Input value={draft.service_filter} onChange={(e) => setDraft({ ...draft, service_filter: e.target.value })} placeholder="e.g. termite-control" /></div>
          <div><Label>Start date</Label><Input type="date" value={draft.start_date} onChange={(e) => setDraft({ ...draft, start_date: e.target.value })} data-testid="camp-start" /></div>
          <div><Label>End date</Label><Input type="date" value={draft.end_date} onChange={(e) => setDraft({ ...draft, end_date: e.target.value })} data-testid="camp-end" /></div>
          <div><Label>Per-job bonus ₹</Label><Input type="number" value={draft.per_job_bonus} onChange={(e) => setDraft({ ...draft, per_job_bonus: e.target.value })} data-testid="camp-bonus" /></div>
        </div>
        <div className="mt-3 flex gap-2">
          <Button onClick={save} disabled={busy || !draft.name || !draft.start_date || !draft.end_date} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="camp-save"><Save size={14} className="mr-1.5" /> {editing ? 'Update' : 'Add campaign'}</Button>
          {editing && <Button variant="outline" onClick={() => { setEditing(null); setDraft(blank); }}><X size={14} /></Button>}
        </div>
      </div>
      <div className="bg-white border rounded-xl shadow-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-600">
            <tr><th className="px-4 py-2 text-left">Campaign</th><th className="px-4 py-2 text-left">Window</th><th className="px-4 py-2 text-left">Filter</th><th className="px-4 py-2 text-left">Bonus / job</th><th className="px-4 py-2"></th></tr>
          </thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={5} className="text-center py-6 text-slate-400">No campaigns yet.</td></tr>}
            {rows.map((r) => (
              <tr key={r.id} className="border-t">
                <td className="px-4 py-2 font-semibold">{r.name}{r.active ? '' : ' (paused)'}</td>
                <td className="px-4 py-2 text-slate-700 text-xs"><Calendar size={11} className="inline mr-1" />{r.start_date} → {r.end_date}</td>
                <td className="px-4 py-2 text-xs">{r.pest_filter ? `pest=${r.pest_filter}` : ''} {r.service_filter ? `· svc=${r.service_filter}` : ''} {!r.pest_filter && !r.service_filter ? 'all jobs' : ''}</td>
                <td className="px-4 py-2 font-bold text-amber-700">₹{Number(r.per_job_bonus).toLocaleString()}</td>
                <td className="px-4 py-2 text-right">
                  <Button size="sm" variant="ghost" onClick={() => { setEditing(r.id); setDraft({ name: r.name, pest_filter: r.pest_filter || '', service_filter: r.service_filter || '', start_date: r.start_date, end_date: r.end_date, per_job_bonus: r.per_job_bonus, active: r.active }); }}><Pencil size={14} /></Button>
                  <Button size="sm" variant="ghost" onClick={() => del(r.id)} className="text-red-600"><Trash2 size={14} /></Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ───── Earnings overview ─────
function EarningsOverviewPanel({ api }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api('GET', '/admin/earnings/overview');
      setRows(r.data?.items || []);
    } finally { setLoading(false); }
  }, [api]);
  useEffect(() => { load(); }, [load]);

  const hold = async (empId) => {
    const reason = window.prompt('Reason for holding this payout?');
    if (!reason) return;
    await api('POST', `/admin/payouts/${empId}/hold`, { reason });
    alert('Payout placed on hold.');
  };
  const release = async (empId) => {
    if (!window.confirm('Release this employee\'s payout hold?')) return;
    await api('POST', `/admin/payouts/${empId}/release`, {});
    alert('Hold released.');
  };

  return (
    <div className="bg-white border rounded-xl shadow-sm overflow-hidden">
      <div className="p-4 border-b text-sm font-semibold text-slate-900">Live earnings this month</div>
      {loading ? <div className="p-6 text-center text-slate-500">Loading…</div> : (
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-600">
            <tr><th className="px-4 py-2 text-left">Technician</th><th className="px-4 py-2 text-left">Jobs</th><th className="px-4 py-2 text-left">Slab</th><th className="px-4 py-2 text-left">Base</th><th className="px-4 py-2 text-left">Campaign</th><th className="px-4 py-2 text-left">×Quality</th><th className="px-4 py-2 text-left">Total</th><th className="px-4 py-2"></th></tr>
          </thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={8} className="text-center py-6 text-slate-400">No active employees.</td></tr>}
            {rows.map((r) => (
              <tr key={r.employee_id} className="border-t" data-testid={`earn-row-${r.employee_id}`}>
                <td className="px-4 py-2"><div className="font-semibold">{r.name}</div><div className="text-xs text-slate-500">{r.employee_code}</div></td>
                <td className="px-4 py-2 font-bold">{r.jobs_completed}</td>
                <td className="px-4 py-2 text-xs">{r.matched_slab || '—'}</td>
                <td className="px-4 py-2">₹{Number(r.base_incentive).toLocaleString()}</td>
                <td className="px-4 py-2">₹{Number(r.campaign_bonus).toLocaleString()}</td>
                <td className="px-4 py-2">× {r.quality_multiplier}</td>
                <td className="px-4 py-2 font-bold text-emerald-700">₹{Number(r.total).toLocaleString()}</td>
                <td className="px-4 py-2 text-right">
                  <Button size="sm" variant="outline" onClick={() => hold(r.employee_id)} className="mr-1"><Pause size={12} className="mr-1" />Hold</Button>
                  <Button size="sm" variant="outline" onClick={() => release(r.employee_id)}><Play size={12} className="mr-1" />Release</Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

// ───── Holds ─────
function HoldsPanel({ api }) {
  const [rows, setRows] = useState([]);
  const load = useCallback(async () => {
    const r = await api('GET', '/admin/payouts/holds');
    setRows(r.data?.items || []);
  }, [api]);
  useEffect(() => { load(); }, [load]);
  const release = async (empId) => { await api('POST', `/admin/payouts/${empId}/release`, {}); load(); };
  return (
    <div className="bg-white border rounded-xl shadow-sm overflow-hidden">
      <div className="p-4 border-b text-sm font-semibold text-slate-900 flex items-center gap-2"><AlertCircle size={16} className="text-amber-600" /> Active payout holds ({rows.length})</div>
      {rows.length === 0 ? (
        <div className="p-6 text-center text-slate-500 text-sm flex items-center justify-center gap-2"><CheckCircle2 size={14} className="text-green-600" /> No active holds — all payouts are flowing.</div>
      ) : (
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-600"><tr><th className="px-4 py-2 text-left">Employee</th><th className="px-4 py-2 text-left">Reason</th><th className="px-4 py-2 text-left">Since</th><th className="px-4 py-2"></th></tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-t">
                <td className="px-4 py-2 font-mono text-xs">{r.employee_id.slice(0, 12)}…</td>
                <td className="px-4 py-2">{r.reason}</td>
                <td className="px-4 py-2 text-xs text-slate-500">{new Date(r.created_at).toLocaleString()}</td>
                <td className="px-4 py-2 text-right"><Button size="sm" onClick={() => release(r.employee_id)} className="bg-emerald-600 hover:bg-emerald-700 text-white"><Play size={12} className="mr-1" />Release</Button></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
